import {Box, Flex, Image, Text, Title} from 'exsportia-components'
import React, {FC, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {StoreCard, useStoreGoods} from '../../api/store'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {ProductQuickBuy} from './product-quick-buy'
import {useCurrencyFormat} from './store-shared'

// exsportia-components' Box and Flex support `overflow` and nothing axis-specific — the string
// "overflowX" does not appear anywhere in the built package — so an `overflowX`/`overflowY` PROP is
// dropped in silence. Passed through `style`, which does reach the DOM (Dima 2026-08-30).
const scrollXStyle: React.CSSProperties = {overflowX: 'auto'}

export const ProductPlaceholder: FC<{size?: number}> = ({size = 32}) => (
  <Flex
    width='100%'
    height='100%'
    alignItems='center'
    justifyContent='center'
  >
    {/* A product glyph, not a broken-image one: the empty square must read as "no photo yet",
        never as "failed to load" (WBA-4594). */}
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='rgba(0, 16, 61, 0.24)'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16.5 9.4 7.55 4.24' />
      <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
      <polyline points='3.29 7 12 12 20.71 7' />
      <line x1='12' x2='12' y1='22' y2='12' />
    </svg>
  </Flex>
)

const GoodsCard: FC<{card: StoreCard; venueId?: string}> = ({card, venueId}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const format = useCurrencyFormat()
  const {openModal} = useModalActions()
  // Tapping a card opens the quick-buy sheet rather than a full navigation:
  // most of the time the shopper only needs a size and an Add (Dima
  // 2026-08-17). The sheet still offers "View details".
  const openQuickBuy = () =>
    openModal({
      // The product name goes inside the panel, not the modal header: a long
      // one rendered as a three-line headline.
      title: l('store-title', {caseAction: 'capitalize'}),
      footerConfig: {disabled: true},
      // A four-chip picker does not need the modal's full default width
      // (Dima 2026-08-19).
      settings: {modalBody: {maxWidth: '440px'}},
      component: (
        <ProductQuickBuy
          venueId={venueId}
          format={format}
          stockItemId={card.uid}
          onGoToCart={() => navigate('../store/cart')}
          onViewDetails={() => navigate(`../store/goods/${card.uid}`)}
        />
      ),
    })
  const price = card.priceMin === null
    ? ''
    : card.priceMin === card.priceMax
    ? format(card.priceMin)
    : `${format(card.priceMin)} – ${format(card.priceMax)}`
  return (
    <Flex
      width={['calc(50% - 8px)', 'calc(33.333% - 11px)']}
      flexDirection='column'
      gap='8px'
      cursor='pointer'
      onClick={openQuickBuy}
    >
      <Box
        width='100%'
        pt='100%'
        position='relative'
        borderRadius='16px'
        overflow='hidden'
        backgroundColor='rgba(0, 16, 61, 0.06)'
      >
        <Box
          position='absolute'
          top='0'
          left='0'
          width='100%'
          height='100%'
        >
          {card.thumbUrl ? (
            <Image
              width='100%'
              height='100%'
              style={{objectFit: 'cover'}}
              src={card.thumbUrl}
            />
          ) : (
            <ProductPlaceholder />
          )}
        </Box>
      </Box>
      <Text text={card.name} />
      <Flex
        gap='8px'
        alignItems='center'
      >
        {card.compareAtPrice !== null && card.priceMin !== null && card.compareAtPrice > card.priceMin && (
          <Text
            text={format(card.compareAtPrice)}
            settings={{textDecoration: 'line-through', color: 'rgba(0, 16, 61, 0.48)', fontSize: '14px'}}
          />
        )}
        <Text
          text={card.inStock ? price : l('store-sold-out', {caseAction: 'capitalize'})}
          settings={{fontWeight: 600, color: card.inStock ? undefined : '#B3261E'}}
        />
      </Flex>
    </Flex>
  )
}

export const StorePage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {venueId} = useParams<{venueId: string}>()
  const {data, isLoading} = useStoreGoods(venueId)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const items = useMemo(
    () => (data?.items || []).filter((card) => categoryId === null || card.categoryId === categoryId),
    [data, categoryId],
  )
  return (
    <GridLayout>
    <Box width='100%'>
      <Flex
        py='8px'
        alignItems='center'
        justifyContent='space-between'
      >
        <Title
          styleType='h1'
          text={l('store-title', {caseAction: 'capitalize'})}
        />
        {/* The cart moved into the app header (icon + count); this slot links
            to the customer's orders instead — link-styled, it is a redirect
            (Dima 2026-08-19). */}
        <Box
          cursor='pointer'
          onClick={() => navigate('../orders')}
        >
          <Text settings={{fontWeight: 600, color: '#0077FF'}}>
            {l('store-orders', {caseAction: 'capitalize'})}
          </Text>
        </Box>
      </Flex>
      {(data?.categories.length || 0) > 0 && (
        <Flex
          gap='8px'
          py='8px'
          style={scrollXStyle}
        >
          <Box flexShrink={0}>
          <Button
            type={categoryId === null ? 'primary' : 'secondary'}
            settings={{width: 'max-content'}}
            onClick={() => setCategoryId(null)}
          >
            {l('store-all-goods', {caseAction: 'capitalize'})}
          </Button>
          </Box>
          {(data?.categories || []).map((category) => (
            <Box
              key={category.uid}
              flexShrink={0}
            >
            <Button
              type={categoryId === category.uid ? 'primary' : 'secondary'}
              settings={{width: 'max-content'}}
              onClick={() => setCategoryId(category.uid)}
            >
              {category.name}
            </Button>
            </Box>
          ))}
        </Flex>
      )}
      <Flex
        mt='8px'
        gap='16px'
        flexWrap='wrap'
      >
        {items.map((card) => (
          <GoodsCard
            key={card.uid}
            card={card}
            venueId={venueId}
          />
        ))}
      </Flex>
      {!isLoading && items.length === 0 && (
        <Flex
          py='48px'
          justifyContent='center'
        >
          <Text
            text={l('store-empty', {caseAction: 'capitalize'})}
            settings={{color: 'rgba(0, 16, 61, 0.48)'}}
          />
        </Flex>
      )}
    </Box>
    </GridLayout>
  )
}
